/**
 * The origination stage model, as the server defines it.
 *
 * `GET /loans/stages` is the one definition of the vocabulary: which stages
 * exist, their order, which are optional and which stage is terminal. A screen
 * that needs to know "is this deal funded?" asks the model rather than typing
 * its own list of stage names — three hand-typed lists in one file once
 * disagreed, so a deal at IC / Committee Approved was badged "Funded" in a
 * table whose header counted it as pipeline.
 *
 * `LOAN_STAGES` (`@/types`) stays as the one typed copy, because the
 * `LoanStage` union is compile-time and a response cannot extend it. It is only
 * the fallback for the moments the served model is not here yet (the request in
 * flight, a refusal, a test), so a screen never flickers or breaks waiting on
 * it. `backend/tests/test_stage_model_copies.py` pins that constant to the
 * server's model and fails if another literal copy of the list appears.
 */

import api from './api'
import { LOAN_STAGES } from '@/types'

/** One stage as `GET /loans/stages` serves it. */
export type StageModelStage = {
  /** Stable code (`triage`, `funded`, …) — what the server keys on. */
  code: string
  /** Display label, and the value a loan's `stage` carries. */
  label: string
  /** Position in the canonical order. */
  index: number
  /** A deal may skip it without its progression being incomplete. */
  optional: boolean
}

export type StageModel = {
  stages: StageModelStage[]
  /** Pre-model stage names (`Received`, …) mapped to the code they now mean. */
  legacyAliases: Record<string, string>
}

/** The code of the terminal stage — the only one that means "funded". */
export const FUNDED_STAGE_CODE = 'funded'

export const stageModelService = {
  /** Already camelCase and keyed by a fixed schema, so it is read verbatim. */
  get: () => api.get('/loans/stages').then((r) => r.data as StageModel),
}

export type DerivedStageModel = {
  /** The server's model is in. False means the constant fallback is showing. */
  loaded: boolean
  /** Stage labels in canonical order. */
  labels: string[]
  /** Label of the stage whose code is `funded`; null if the model has none. */
  fundedLabel: string | null
  /** True only for the terminal `funded` stage (or a legacy name that maps to it). */
  isFunded: (stage: string | null | undefined) => boolean
}

/**
 * Reduce a served model to what screens ask of it. Pure, so a test can pin the
 * rule. With no model — still loading, refused, or an empty payload — it falls
 * back to `LOAN_STAGES`, whose last element is the funded stage.
 */
export function deriveStageModel(model?: StageModel | null): DerivedStageModel {
  const served = model && Array.isArray(model.stages) && model.stages.length > 0 ? model : null

  if (!served) {
    const fundedLabel = LOAN_STAGES[LOAN_STAGES.length - 1] ?? null
    return {
      loaded: false,
      labels: [...LOAN_STAGES],
      fundedLabel,
      isFunded: (stage) => fundedLabel !== null && stage === fundedLabel,
    }
  }

  const ordered = [...served.stages].sort((a, b) => a.index - b.index)
  const funded = ordered.find((s) => s.code === FUNDED_STAGE_CODE) ?? null
  const fundedLabel = funded ? funded.label : null
  const aliases = served.legacyAliases ?? {}
  return {
    loaded: true,
    labels: ordered.map((s) => s.label),
    fundedLabel,
    isFunded: (stage) => {
      if (!stage || !funded) return false
      return stage === funded.label || aliases[stage] === FUNDED_STAGE_CODE
    },
  }
}
